import React, { useState, useEffect } from 'react';
import { Copy, Check, Wallet } from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const TokenWallets = ({ showAddressOnMobile = false }) => {
  const { tokenId } = useParams();
  const [currentTokenWallet, setCurrentTokenWallet] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tokenId) {
      fetchCurrentTokenWallet();
    } else {
      setCurrentTokenWallet(null);
      setLoading(false);
    }
  }, [tokenId]);

  const fetchCurrentTokenWallet = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/user/token-wallets`);
      const wallets = response.data;
      
      // Find the wallet for the current token
      const currentWallet = wallets.find(wallet => wallet.token_id === tokenId && wallet.wallet_address);
      setCurrentTokenWallet(currentWallet || null);
      setError(null);
    } catch (error) {
      console.error('Error fetching token wallet:', error);
      setError('Failed to load wallet');
      setCurrentTokenWallet(null);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'No wallet';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Only show wallet if we're on a token page and the wallet exists
  if (loading) {
    return null; // Don't show loading spinner in navbar
  }

  if (error || !currentTokenWallet || !tokenId) {
    return null; // Don't show anything if no current token wallet
  }

  return (
    <div className="flex items-center space-x-1 xs:space-x-2 px-1 xs:px-2 lg:px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-md">
      <Wallet className="w-4 h-4 text-gray-600 dark:text-gray-300" />

      <div className="flex items-center space-x-1 xs:space-x-2">
        {/* Show formatted address only on lg+ screens, or always if showAddressOnMobile is true */}
        <span className={`${showAddressOnMobile ? 'inline' : 'hidden lg:inline'} text-sm font-medium text-gray-700 dark:text-gray-200`}>
          {formatAddress(currentTokenWallet.wallet_address)}
        </span>

        <button
          onClick={() => copyToClipboard(currentTokenWallet.wallet_address)}
          className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title={`Copy wallet address: ${currentTokenWallet.wallet_address}`}
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-600" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      </div>
    </div>
  );
};

export default TokenWallets;